import { AlertTriangle, Shuffle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ISSUE_DEFS } from "@/hooks/useAnaesthesiaSim2";

// Simulated-complications panel: Manual mode shows the labelled trigger list;
// Random mode hides it, shows the "armed" banner (with breed-weighted bias) and
// auto-fires complications via the parent's interval.
export const ComplicationsPanel = ({
  randomMode,
  setRandomMode,
  vulnerabilities,
  breed,
  running,
  activeIssueKeys,
  issues,
  triggerIssue,
}) => (
  <Card className="border-slate-200 p-5" data-testid="issues-card">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
        Simulated complications
      </div>
      <label
        className="flex items-center gap-2 cursor-pointer select-none"
        data-testid="random-mode-toggle-label"
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          {randomMode ? "Random" : "Manual"}
        </span>
        <Switch
          checked={randomMode}
          onCheckedChange={setRandomMode}
          data-testid="random-mode-toggle"
        />
      </label>
    </div>
    {randomMode ? (
      <div
        data-testid="random-mode-banner"
        className="rounded-md border-2 border-amber-300 bg-amber-50/60 p-4 text-center"
      >
        <Shuffle className="w-5 h-5 mx-auto text-amber-600 mb-1.5" />
        <div className="text-sm font-semibold text-slate-900">
          Random complications armed
        </div>
        <div className="text-[11px] text-slate-600 mt-1 leading-relaxed">
          The list is hidden. While the monitor is running, complications strike at
          random — watch the alarm bar and vitals, recognise the problem and treat it.
        </div>
        {vulnerabilities.length > 0 && (
          <div
            className="mt-2 text-[11px] text-amber-700 font-semibold leading-snug"
            data-testid="random-bias-list"
          >
            Weighted toward {breed} risks:{" "}
            {vulnerabilities.map((k) => ISSUE_DEFS[k].label).join(" · ")}
          </div>
        )}
        <div className="text-[10px] font-mono-clinical mt-2 uppercase tracking-wider">
          {running ? (
            <span className="text-emerald-600">● Live — armed</span>
          ) : (
            <span className="text-slate-400">Paused — resume to arm</span>
          )}
        </div>
        {activeIssueKeys.length > 0 && (
          <div
            className="mt-2 text-[11px] text-red-600 font-semibold"
            data-testid="random-active-list"
          >
            Active: {activeIssueKeys.map((k) => ISSUE_DEFS[k].label).join(" · ")}
          </div>
        )}
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-2">
        {Object.entries(ISSUE_DEFS).map(([k, def]) => {
          const active = issues[k];
          const Icon = def.icon;
          return (
            <button
              key={k}
              onClick={() => triggerIssue(k)}
              disabled={active}
              data-testid={`trigger-${k}-btn`}
              className={`flex items-center justify-between gap-3 border-2 rounded-md px-3 py-2 text-left transition-all ${
                active
                  ? "border-red-400 bg-red-50 cursor-not-allowed"
                  : "border-slate-200 bg-white hover:border-red-300 hover:bg-red-50/40"
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Icon
                  className={`w-4 h-4 flex-shrink-0 ${
                    active ? "text-red-600" : "text-slate-500"
                  }`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    {def.label}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {def.fixHint}
                  </div>
                </div>
              </div>
              {active ? (
                <Badge className="bg-red-600 text-white text-[10px] alarm-pulse">
                  ACTIVE
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">
                  Trigger
                </Badge>
              )}
            </button>
          );
        })}
      </div>
    )}
  </Card>
);

export default ComplicationsPanel;
